import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Stack, Typography } from "@material-ui/core";
import {
  Forum,
  Autorenew,
  CheckCircle,
  Cancel,
  Flag,
  AddCircleOutline,
  TaskAlt,
} from "@material-ui/icons";
import { propuestasApi } from "src/services/propuestasApi";
import { formatearFecha } from "src/utils/formatTime";
import { BRAND } from "src/theme/brandTokens";

// Traduce el nombre crudo de una columna de propuesta_items (o de los dos
// pseudo-campos de alta/baja de un servicio) a una etiqueta legible. Cubre
// los campos versionables que el backend registra en la bitácora
// (renegociacionPropuesta.js: CAMPOS_VERSIONABLES + item_agregado/
// item_retirado). Un nombre no listado cae al crudo en vez de desaparecer:
// un campo nuevo que el backend empiece a versionar mañana sigue siendo
// visible aunque nadie haya actualizado este mapa todavía.
const CAMPO_LABELS = {
  nombre_servicio: "Nombre del servicio",
  tipo_moneda: "Moneda",
  valor_original: "Precio",
  descuento: "Descuento",
  valor_con_descuento: "Precio con descuento",
  fecha_termino_desc: "Término del descuento",
  tipo_frecuencia: "Frecuencia",
  fecha_siguiente_ciclo: "Próximo ciclo",
  fecha_limite: "Fecha límite",
  fase_titulo: "Título",
  fase_objetivo: "Objetivo",
  fase_actividades: "Actividades",
  fase_entregable: "Entregable",
  fase_duracion: "Duración",
  item_agregado: "Servicio agregado",
  item_retirado: "Servicio retirado",
};

const labelCampo = (campo) => CAMPO_LABELS[campo] ?? campo;

const DECISION_LABELS = {
  ACEPTADO: "Aceptó",
  RECHAZADO: "Rechazó",
  OBSERVADO: "Observó",
};

const AUTOR_LABELS = {
  CLIENTE: "Cliente",
  VENDEDOR: "Vendedor",
  SISTEMA: "Sistema",
};

const formatFecha = (fecha) => {
  if (!fecha) return "—";
  try {
    return formatearFecha(fecha, "DD [de] MMM YYYY, HH:mm");
  } catch {
    return String(fecha);
  }
};

// Texto de una línea del delta de una ronda. item_agregado/item_retirado no
// tienen "valor anterior -> valor nuevo": son un alta o una baja, así que se
// narran distinto del resto de los campos negociados.
const textoDelta = (d) => {
  if (d.campo === "item_agregado") {
    return `Servicio agregado: ${d.valorNuevo ?? d.nombreServicio ?? "—"}`;
  }
  if (d.campo === "item_retirado") {
    return `Servicio retirado: ${d.valorAnterior ?? d.nombreServicio ?? "—"}`;
  }
  const prefijo = d.nombreServicio ? `${d.nombreServicio} — ` : "";
  const anterior = d.valorAnterior ?? "—";
  const nuevo = d.valorNuevo ?? "—";
  return `${prefijo}${labelCampo(d.campo)}: ${anterior} -> ${nuevo}`;
};

// Título e icono de un hecho de la bitácora (decisión del cliente, solicitud
// de un servicio nuevo o cierre explícito). Son los únicos tres tipos que el
// backend expone en /hilo (CAMPOS_DE_HECHO).
const descripcionHecho = (h) => {
  if (h.tipo === "decision_cliente") {
    const verbo = DECISION_LABELS[h.decision] ?? h.decision ?? "Respondió";
    const servicio = h.nombreServicio ? ` — ${h.nombreServicio}` : "";
    const iconoPorDecision = {
      ACEPTADO: { Icon: CheckCircle, color: "#1B5E20" },
      RECHAZADO: { Icon: Cancel, color: "#B71C1C" },
      OBSERVADO: { Icon: Flag, color: "#F57F17" },
    };
    return {
      titulo: `${verbo}${servicio}`,
      ...(iconoPorDecision[h.decision] ?? { Icon: Flag, color: "#9E9E9E" }),
    };
  }
  if (h.tipo === "servicio_solicitado") {
    return {
      titulo: "Solicitó un nuevo servicio",
      Icon: AddCircleOutline,
      color: "#00695C",
    };
  }
  if (h.tipo === "cierre_explicito") {
    return {
      titulo: "Cierre explícito de la propuesta",
      Icon: TaskAlt,
      color: "#455A64",
    };
  }
  return { titulo: h.tipo, Icon: Flag, color: "#9E9E9E" };
};

// El registro de una decisión del cliente siempre queda con autor_tipo
// CLIENTE aunque la teclee el vendedor por un canal externo, porque el hecho
// es del cliente. Pero el autor **sí** viaja en ese caso: el canal interno
// guarda el RUT de quien lo registró, así que ese nombre existe y se muestra.
// No borres la rama que lo usa creyendo que nunca llega -- se perdería la
// atribución de quién dejó registrada la decisión, que es justamente lo que
// respalda un registro hecho por fuera del portal.
const autorTexto = (h) => {
  if (h.autorNombre) return `Registrado por ${h.autorNombre}`;
  if (h.tipo === "decision_cliente") return null;
  const label = AUTOR_LABELS[h.autorTipo];
  return label ? `Registrado por ${label}` : null;
};

const Dot = ({ color, Icon }) => (
  <Box
    sx={{
      width: 30,
      height: 30,
      borderRadius: "50%",
      bgcolor: color,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Icon sx={{ fontSize: 17 }} />
  </Box>
);

Dot.propTypes = {
  color: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
};

/**
 * Hilo de la negociación de una propuesta V2: las rondas enviadas (con el
 * delta que las separa de la anterior) intercaladas en el tiempo con los
 * hechos de la bitácora — decisiones del cliente, servicios solicitados por
 * fuera y el cierre explícito. Es una vista de lo que pasó, nunca del estado
 * vigente: ese sale de `propuesta.estado`, no de acá.
 *
 * No se muestra si no hay nada que contar todavía: la ronda 1 no tiene delta
 * (es el contenido con el que arrancó la conversación, no una diferencia) y
 * una propuesta sin hechos registrados no tiene hilo, tiene solo un envío,
 * que ya cubre TrackingTimeline.
 */
const HiloNegociacion = ({ propuestaId }) => {
  const { data, isLoading, error } = propuestasApi.useGetHiloNegociacionV2Query(
    { id: propuestaId },
  );

  const entradas = useMemo(() => {
    const deRondas = (data?.rondas ?? [])
      .filter((ronda) => Array.isArray(ronda.delta) && ronda.delta.length > 0)
      .map((ronda) => ({
        tipo: "ronda",
        fecha: ronda.fechaEnvio,
        ronda,
      }));
    const deHechos = (data?.hechos ?? []).map((hecho) => ({
      tipo: "hecho",
      fecha: hecho.fecha,
      hecho,
    }));
    return [...deRondas, ...deHechos].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha),
    );
  }, [data]);

  // El contrato del componente es ser invisible cuando no hay nada que
  // contar. Mientras la consulta está en vuelo (o si falla) todavía no se
  // sabe si hay algo, así que tampoco se dibuja contenedor: mostrar una
  // tarjeta vacía con spinner (o de error) en una propuesta sin historial
  // rompería ese contrato con un elemento que no lleva a ningún resultado.
  if (isLoading || error) return null;

  if (entradas.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Forum fontSize="small" color="action" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Historial de negociación
        </Typography>
      </Stack>

      <Stack spacing={0}>
        {entradas.map((entrada, idx) => {
          const isLast = idx === entradas.length - 1;
          if (entrada.tipo === "ronda") {
            const { ronda } = entrada;
            const origen =
              ronda.canalOrigen === "CLIENTE"
                ? "Origen: solicitud del cliente"
                : ronda.canalOrigen === "VENDEDOR"
                  ? "Origen: ajuste del vendedor"
                  : null;
            return (
              <Stack
                key={`ronda-${ronda.numero}`}
                direction="row"
                alignItems="flex-start"
                sx={{ mb: isLast ? 0 : 2 }}
              >
                <Box sx={{ mr: 1.5 }}>
                  <Dot color={BRAND.color.primary} Icon={Autorenew} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Ronda {ronda.numero} enviada
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block" }}
                  >
                    {formatFecha(ronda.fechaEnvio)}
                    {origen ? ` · ${origen}` : ""}
                  </Typography>
                  {ronda.motivo && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", fontStyle: "italic", mt: 0.25 }}
                    >
                      “{ronda.motivo}”
                    </Typography>
                  )}
                  <Stack component="ul" sx={{ pl: 2.5, m: 0, mt: 0.75 }}>
                    {ronda.delta.map((d, i) => (
                      <Typography
                        component="li"
                        key={`${ronda.numero}-${d.refItem ?? "sin-item"}-${d.campo}-${i}`}
                        variant="body2"
                      >
                        {textoDelta(d)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            );
          }

          const { hecho } = entrada;
          const { titulo, Icon, color } = descripcionHecho(hecho);
          const autor = autorTexto(hecho);
          return (
            <Stack
              key={`hecho-${hecho.tipo}-${hecho.fecha}-${hecho.refItem ?? "sin-item"}`}
              direction="row"
              alignItems="flex-start"
              sx={{ mb: isLast ? 0 : 2 }}
            >
              <Box sx={{ mr: 1.5 }}>
                <Dot color={color} Icon={Icon} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {titulo}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block" }}
                >
                  {formatFecha(hecho.fecha)}
                  {autor ? ` · ${autor}` : ""}
                </Typography>
                {hecho.comentario && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 0.25, fontStyle: "italic" }}
                  >
                    “{hecho.comentario}”
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

HiloNegociacion.propTypes = {
  propuestaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default HiloNegociacion;
